import React, { ElementType } from 'react';
import classnames from 'classnames';
import { FormControl } from 'react-bootstrap';
import { Control, Controller, useWatch } from 'react-hook-form';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import { useSiteSettings } from 'v2/queries/useSiteSettings';
import styles from './textDropDownPair.module.scss';

export type SelectOption = { label: string; value: string | number };

export interface UnitComponentProps {
  name: string;
  options: SelectOption[];
  onChange: (value?: string | number | null) => void;
  prefix?: string;
  value?: string | number;
  warning: boolean;
}

export interface TextDropDownPairFilterFreeUserProps {
  label?: string;
  error?: string | null;
  className?: string;
  required?: boolean;
  // We don't know what are the form fields for this control
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  name: string;
  type?: 'text' | 'number';
  placeholder?: string;
  prefix?: string;
  options: SelectOption[];
  component?: ElementType<UnitComponentProps>;
  defaultValue: { unit: string | number | null; value: string };
}

const SelectComponent = ({
  name,
  prefix,
  onChange,
  value,
  options,
  warning,
}: UnitComponentProps) => {
  return (
    <select
      data-cy="dropdown"
      onChange={(e) => onChange(e.currentTarget.value)}
      className={classnames(styles.DropDown, {
        [styles.withPrefix]: !!prefix,
        [styles.warning]: warning,
      })}
      aria-label={`${name}-unit`}
      value={value}
    >
      {options.map((option) => (
        <option
          key={option.value}
          value={option.value}
          className={classnames(styles.text)}
        >
          {option.label}
        </option>
      ))}
    </select>
  );
};

const TextDropDownPairFilterFreeUser = ({
  label,
  required,
  error,
  name,
  control,
  type = 'text',
  placeholder = '0',
  prefix,
  options,
  component: Component = SelectComponent,
  defaultValue,
}: TextDropDownPairFilterFreeUserProps) => {
  const { data } = useSiteSettings();

  const minimumPriceWarningForMaximumPriceFilter =
    data?.minimum_price_warning_for_maximum_price_filter ?? 0;

  const { value: maximumPrice } = useWatch({ control, name: 'filters.price' });

  const warningPriceBelowSpecifiedValue =
    +maximumPrice < minimumPriceWarningForMaximumPriceFilter;
  return (
    <>
      {label ? (
        <FormLabel label={label} required={required} error={error} />
      ) : null}
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => (
          <>
            <div
              className="d-flex"
              data-cy="input-and-dropdown-grouped-container"
            >
              <div className={styles.inputGroup} data-cy="text-input-field">
                {prefix ? (
                  <span className={styles.prefix}>{prefix}</span>
                ) : null}
                <FormControl
                  type={type}
                  className={classnames(styles.Input, {
                    [styles.withPrefix]: !!prefix,
                    [styles.warning]: warningPriceBelowSpecifiedValue,
                  })}
                  value={field.value?.value}
                  onChange={(e) =>
                    field.onChange({
                      ...field.value,
                      value: e.currentTarget.value,
                    })
                  }
                  placeholder={placeholder}
                  aria-label={`${name}-value`}
                />
              </div>

              <div data-cy="dropdown-field">
                <Component
                  name={name}
                  prefix={prefix}
                  options={options}
                  onChange={(value) =>
                    field.onChange({
                      ...field.value,
                      unit: value,
                    })
                  }
                  value={field.value?.unit}
                  warning={warningPriceBelowSpecifiedValue}
                />
              </div>
            </div>
            {warningPriceBelowSpecifiedValue && (
              <div className={styles.warningMsg}>
                At such a low price point you are unlikely to get any responses
                to your request.
              </div>
            )}
          </>
        )}
      />
    </>
  );
};

export default TextDropDownPairFilterFreeUser;
